import {
  ALLOCATED_COLOR_NO_OPACITY_RGBA,
  ALLOCATED_COLOR_RGB,
  ALLOCATED_COLOR_RGBA,
  AVAILABLE_COLOR_NO_OPACITY_RGBA,
  AVAILABLE_COLOR_RGB,
  AVAILABLE_COLOR_RGBA,
  DISABLE_COLOR_RGB,
  DISABLE_COLOR_RGBA,
  PROGRESS_BAR_BACKGROUND_COLOR_RGBA,
  SELECTED_COLOR_RGBA,
  TRANSPARENT_COLOR_RGBA,
  UNAVAILABLE_COLOR_ORANGE_RGBA,
  UNAVAILABLE_COLOR_RGB,
  UNAVAILABLE_COLOR_RGBA,
  UNAVAILABLE_ORANGE_COLOR_RGB,
} from '../utils/mapColors';
import { MAP_ELEMENT_RADIUS_SCALE } from '../utils/getMapElementSize';
import AvailableAllocatedImpressionsLayer from './AvailableAllocatedImpressionsLayer';
import { getAssetBaseRgb, getClusterState } from './utils';

export const createAvailableAllocationRatioLayer = (
  mapData,
  routeFrameCodes,
  {
    impressions: { availableImpressions, allocatedImpressions },
    clusters: { isImpressionsClusterVisible, isAvailabilityClusterVisible, isAllocationClusterVisible },
    rangeSegments,
    isReadOnly,
    isDirectSales,
    isPriced,
    isSharedCampaign,
    activeColorFilter,
    shouldShowSelectionState,
  },
  getPosition,
  getMapElementSize,
  updateTriggers,
) => {
  const getAvailableImpressionsRatio = (data) => {
    if (isDirectSales && !isPriced && !isSharedCampaign) return 1;

    if (
      !availableImpressions ||
      (data.properties.cluster && !data.properties.sumOfAvailableImpressionsForCluster) ||
      (data.properties.cluster && !data.properties.sumOfTotalImpressionsForCluster) ||
      (!data.properties.cluster && !data.properties.totalImpressions) ||
      (!data.properties.cluster && !data.properties.availableImpressions)
    )
      return 0;

    return data.properties.cluster
      ? data.properties.sumOfAvailableImpressionsForCluster / data.properties.sumOfTotalImpressionsForCluster
      : (data.properties.availableImpressions ?? data.properties.impressions) / data.properties.totalImpressions;
  };

  const getAllocatedImpressionsRatio = (data) => {
    if (isDirectSales && isPriced && !isSharedCampaign) return 1;

    if (
      !allocatedImpressions ||
      (data.properties.cluster && !data.properties.sumOfAllocatedImpressionsForCluster) ||
      (data.properties.cluster && !data.properties.sumOfTotalImpressionsForCluster) ||
      (!data.properties.cluster && !data.properties.totalImpressions) ||
      (!data.properties.cluster && !data.properties.allocatedImpressions)
    )
      return 0;

    return data.properties.cluster
      ? data.properties.sumOfAllocatedImpressionsForCluster / data.properties.sumOfTotalImpressionsForCluster
      : data.properties.allocatedImpressions / data.properties.totalImpressions;
  };

  const getIsVisible = (data) => Number(data.isVisible || data.properties.isVisible ? 1 : 0);

  const colorConfig = {
    isSharedCampaign,
    isReadOnly,
    isDirectSales,
    impressions: { allocatedImpressions, availableImpressions },
    rangeSegments,
    routeFrameCodes,
    isPriced,
    activeColorFilter,
  };

  const isSameRgbWithoutAlpha = (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

  const getSelectableAssetColor = (data, isAllocated) => {
    const { isEverySelected, isPartiallySelected } = getClusterState(data, routeFrameCodes);

    if (isAllocated) return isEverySelected ? ALLOCATED_COLOR_NO_OPACITY_RGBA : ALLOCATED_COLOR_RGBA;
    if (shouldShowSelectionState && (isEverySelected || isPartiallySelected)) return SELECTED_COLOR_RGBA;

    return isEverySelected ? AVAILABLE_COLOR_NO_OPACITY_RGBA : AVAILABLE_COLOR_RGBA;
  };

  const getAssetColor = (data) => {
    const baseRgb = getAssetBaseRgb(data, colorConfig);

    if (isSameRgbWithoutAlpha(baseRgb, DISABLE_COLOR_RGB)) return DISABLE_COLOR_RGBA;
    if (isSameRgbWithoutAlpha(baseRgb, UNAVAILABLE_ORANGE_COLOR_RGB)) return UNAVAILABLE_COLOR_ORANGE_RGBA;
    if (isSameRgbWithoutAlpha(baseRgb, UNAVAILABLE_COLOR_RGB)) return UNAVAILABLE_COLOR_RGBA;

    const isAllocated = isSameRgbWithoutAlpha(baseRgb, ALLOCATED_COLOR_RGB);
    if (isAllocated || isSameRgbWithoutAlpha(baseRgb, AVAILABLE_COLOR_RGB))
      return getSelectableAssetColor(data, isAllocated);

    return [...baseRgb, AVAILABLE_COLOR_RGBA[3]];
  };

  const getFirstBarColor = (data) => {
    const rangeColor = rangeSegments?.getSegmentRGBColorByIndexValue(data.index);
    if (rangeColor) {
      return rangeColor;
    }

    if (getAvailableImpressionsRatio(data) > 0) {
      return AVAILABLE_COLOR_RGBA;
    }

    if (getAllocatedImpressionsRatio(data) > 0) {
      return ALLOCATED_COLOR_RGBA;
    }

    return TRANSPARENT_COLOR_RGBA;
  };

  const getSecondBarColor = (data) => {
    if (rangeSegments?.getSegmentRGBColorByIndexValue(data.index) || getAllocatedImpressionsRatio(data) <= 0) {
      return TRANSPARENT_COLOR_RGBA;
    }

    return ALLOCATED_COLOR_RGBA;
  };

  return new AvailableAllocatedImpressionsLayer({
    id: 'available-allocation-ratio-layer',
    data: mapData,
    pickable: true,
    isAvailabilityClusterVisible,
    isAllocationClusterVisible,
    isImpressionsClusterVisible,
    updateTriggers,
    getPosition,
    getMapElementSize,
    getRadius: (d) => getMapElementSize(d) * MAP_ELEMENT_RADIUS_SCALE,
    getFillColor: () => PROGRESS_BAR_BACKGROUND_COLOR_RGBA,
    getAvailableImpressionsRatio,
    getAllocatedImpressionsRatio,
    getIsVisible,
    getAssetColor,
    getFirstBarColor,
    getSecondBarColor,
  });
};
